import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const CHALLENGE_CATEGORIES = [
  'shape.cat.knowledge',
  'shape.cat.reporting',
  'shape.cat.integration',
  'shape.cat.data',
  'shape.cat.automation',
  'shape.cat.software',
  'shape.cat.ai',
  'shape.cat.other',
];

const PROJECT_STAGES = [
  'shape.stage.exploring',
  'shape.stage.defined',
  'shape.stage.concept',
  'shape.stage.ready',
  'shape.stage.existing',
];

export default function ShapeYourProjectPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;

    if (honeypot && honeypot.trim()) {
      setSubmitted(true);
      return;
    }

    // Remove honeypot from the payload before submitting
    formData.delete('company_alt');

    setIsSubmitting(true);
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/da8a0gptacug2b80seqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as never).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        (parsed?.meta as { message?: string; detail?: string } | undefined)?.message ||
        (parsed?.meta as { message?: string; detail?: string } | undefined)?.detail ||
        (parsed?.message as string | undefined) ||
        responseText;

      if (response.ok && code === 'OK') {
        setSubmitted(true);
        setFormError('');
      } else {
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : t('shape.error.generic'),
        );
      }
    } catch {
      setFormError(t('shape.error.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background-50">
        <div className="pt-28 md:pt-36 pb-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-mono font-medium text-accent-600 uppercase tracking-widest mb-4">
                {t('shape.badge')}
              </span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight mb-4">
                {t('shape.title')}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl mx-auto">
                {t('shape.subtitle')}
              </p>
            </div>

            {submitted ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-2xl text-accent-600" />
                </div>
                <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">
                  {t('shape.success.title')}
                </h2>
                <p className="text-foreground-600 max-w-md mx-auto">{t('shape.success.text')}</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-6 bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/60"
                data-readdy-form
              >
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('shape.name.label')} <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors"
                    placeholder={t('shape.name.placeholder')}
                  />
                </div>

                {/* What are you trying to solve */}
                <div>
                  <label htmlFor="challenge" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('shape.challenge.label')} <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="challenge"
                    name="challenge"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors resize-none"
                    placeholder={t('shape.challenge.placeholder')}
                  />
                  <p className="mt-1 text-xs text-foreground-500">{t('shape.challenge.max')}</p>
                </div>

                {/* Challenge category */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('shape.category.label')} <span className="text-accent-600">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors appearance-none"
                  >
                    <option value="">{t('shape.category.placeholder')}</option>
                    {CHALLENGE_CATEGORIES.map((cat) => (
                      <option key={cat} value={t(cat)}>
                        {t(cat)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Systems involved */}
                <div>
                  <label htmlFor="systems" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('shape.systems.label')}
                  </label>
                  <input
                    id="systems"
                    name="systems"
                    type="text"
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors"
                    placeholder={t('shape.systems.placeholder')}
                  />
                </div>

                {/* Project stage */}
                <div>
                  <label htmlFor="stage" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('shape.stage.label')} <span className="text-accent-600">*</span>
                  </label>
                  <select
                    id="stage"
                    name="stage"
                    required
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors appearance-none"
                  >
                    <option value="">{t('shape.stage.placeholder')}</option>
                    {PROJECT_STAGES.map((stage) => (
                      <option key={stage} value={t(stage)}>
                        {t(stage)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contact info grid */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-foreground-800 mb-2">
                      {t('shape.company.label')} <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors"
                      placeholder={t('shape.company.placeholder')}
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-foreground-800 mb-2">
                      {t('shape.role.label')} <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="role"
                      name="role"
                      type="text"
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors"
                      placeholder={t('shape.role.placeholder')}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-foreground-800 mb-2">
                      {t('shape.country.label')} <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="country"
                      name="country"
                      type="text"
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors"
                      placeholder={t('shape.country.placeholder')}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-2">
                      {t('shape.email.label')} <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors"
                      placeholder={t('shape.email.placeholder')}
                    />
                  </div>
                </div>

                {/* Contact number */}
                <div>
                  <label htmlFor="contact" className="block text-sm font-medium text-foreground-800 mb-2">
                    {t('shape.contact.label')}
                  </label>
                  <input
                    id="contact"
                    name="contact"
                    type="tel"
                    className="w-full px-4 py-3 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-accent-400 transition-colors"
                    placeholder={t('shape.contact.placeholder')}
                  />
                </div>

                {/* Honeypot field */}
                <div className="honeypot-field">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {formError}
                  </div>
                )}

                {/* Submit */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t('shape.submitting') : t('shape.badge')}
                  </button>
                  <p className="mt-3 text-xs text-foreground-500">{t('shape.privacy')}</p>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}